<template>
  <b-modal
    :id="id"
    @show="onShow"
    no-close-on-backdrop
  >
    <template #modal-header>
      <modal-close-button @confirmed="closeModal" />
      <h4 class="modal-title mx-auto">{{ $t("global.relationstype") }}</h4>
    </template>

    <!-- Übersetzungs-Dropdowns -->
    <div class="row d-flex justify-content-between mb-2">
      <div class="col-xl-5 form-group">
        <v-select
          v-model="sourceLanguage"
          label="bezeichnung"
          :options="languageList"
          :placeholder="$t('global.sourcelanguage')"
        >
          <span slot="no-options">{{ $t("global.taptosearch") }}</span>
        </v-select>
        <label>{{ $t("global.sourcelanguage") }}</label>
      </div>
      <div class="col-xl-2 text-center my-auto">
        {{ $t("global.to") }}
      </div>
      <div class="col-xl-5 form-group">
        <v-select
          v-model="targetLanguages"
          label="bezeichnung"
          :options="availableTargetLanguages"
          :placeholder="$t('global.targetlanguages')"
          :disabled="!sourceLanguage"
          multiple
        >
          <span slot="no-options">{{ $t("global.taptosearch") }}</span>
        </v-select>
        <label>{{ $t("global.targetlanguages") }}</label>
      </div>
    </div>

    <!-- Bezeichnung (Richtung 1) -->
    <div class="row">
      <div class="col-12">
        <label class="font-weight-bold">{{
          $t("global.designation") + " (" + $t("global.direction") + " 1)"
        }}</label>
      </div>
      <div class="col-6">
        <div class="form-group">
          <input v-model="item.de" class="form-control input" :class="{ 'border-danger': errors.de }" type="text" placeholder=" " />
          <label>{{ $t("global.designation") }} - DE</label>
        </div>
      </div>
      <div class="col-6">
        <div class="form-group">
          <input v-model="item.en" class="form-control input" :class="{ 'border-danger': errors.en }" type="text" placeholder=" " />
          <label>{{ $t("global.designation") }} - EN</label>
        </div>
      </div>
      <div class="col-6">
        <div class="form-group">
          <input v-model="item.fr" class="form-control input" :class="{ 'border-danger': errors.fr }" type="text" placeholder=" " />
          <label>{{ $t("global.designation") }} - FR</label>
        </div>
      </div>
      <div class="col-6">
        <div class="form-group">
          <input v-model="item.it" class="form-control input" :class="{ 'border-danger': errors.it }" type="text" placeholder=" " />
          <label>{{ $t("global.designation") }} - IT</label>
        </div>
      </div>
    </div>

    <hr />

    <!-- Bezeichnung2 (Richtung 2) -->
    <div class="row">
      <div class="col-12">
        <label class="font-weight-bold">{{
          $t("global.designation") + " 2 (" + $t("global.direction") + " 2)"
        }}</label>
      </div>
      <div class="col-6">
        <div class="form-group">
          <input v-model="item.de2" class="form-control input" :class="{ 'border-danger': errors.de2 }" type="text" placeholder=" " />
          <label>{{ $t("global.designation") + " 2" }} - DE</label>
        </div>
      </div>
      <div class="col-6">
        <div class="form-group">
          <input v-model="item.en2" class="form-control input" :class="{ 'border-danger': errors.en2 }" type="text" placeholder=" " />
          <label>{{ $t("global.designation") + " 2" }} - EN</label>
        </div>
      </div>
      <div class="col-6">
        <div class="form-group">
          <input v-model="item.fr2" class="form-control input" :class="{ 'border-danger': errors.fr2 }" type="text" placeholder=" " />
          <label>{{ $t("global.designation") + " 2" }} - FR</label>
        </div>
      </div>
      <div class="col-6">
        <div class="form-group">
          <input v-model="item.it2" class="form-control input" :class="{ 'border-danger': errors.it2 }" type="text" placeholder=" " />
          <label>{{ $t("global.designation") + " 2" }} - IT</label>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <button class="btn btn-default mr-2" @click="closeModal">
        {{ $t("global.cancel") }}
      </button>
      <button
        class="btn btn-primary mr-2"
        :disabled="!sourceLanguage || targetLanguages.length === 0 || isTranslating"
        @click="translate"
      >
        {{ $t("global.translate") }}
      </button>
      <button
        class="btn btn-primary"
        :disabled="!item.de && !item.en && !item.fr && !item.it && !item.de2 && !item.en2 && !item.fr2 && !item.it2"
        @click="save"
      >
        {{ $t("global.save") }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import AppApi from "@/api/AppApi";
import AuswahlwerteApi from "@/api/AuswahlwerteApi";
import { apiErrorToAlert } from "@/utils/Errorhandler";
import ModalCloseButton from "@/components/global/ModalCloseButton";

const LANGUAGES = [
  { kuerzel: "de", bezeichnung: "DE", deepl_kuerzel: "de" },
  { kuerzel: "en", bezeichnung: "EN", deepl_kuerzel: "en" },
  { kuerzel: "fr", bezeichnung: "FR", deepl_kuerzel: "fr" },
  { kuerzel: "it", bezeichnung: "IT", deepl_kuerzel: "it" },
];

const EMPTY = () => ({
  id: null,
  de: null, en: null, fr: null, it: null,
  de2: null, en2: null, fr2: null, it2: null,
});

export default {
  name: "RelationstypModal",
  components: { ModalCloseButton },
  props: {
    id: { type: String, default: "relationstyp-modal" },
    selectedItem: { type: Object, default: null },
  },
  data() {
    return {
      item: EMPTY(),
      errors: { de: false, en: false, fr: false, it: false, de2: false, en2: false, fr2: false, it2: false },
      languageList: LANGUAGES,
      sourceLanguage: null,
      targetLanguages: [],
      isTranslating: false,
    };
  },
  computed: {
    availableTargetLanguages() {
      if (!this.sourceLanguage) return [];
      return LANGUAGES.filter((l) => l.kuerzel !== this.sourceLanguage.kuerzel);
    },
    canTranslate() {
      const langs = ["de", "en", "fr", "it"];
      return langs.every((lang) => {
        const has1 = !!this.item[lang];
        const has2 = !!this.item[lang + "2"];
        return (has1 && has2) || (!has1 && !has2);
      });
    },
  },
  watch: {
    selectedItem(val) {
      this.loadItem(val);
    },
    sourceLanguage() {
      this.targetLanguages = [];
    },
  },
  methods: {
    onShow() {
      this.errors = { de: false, en: false, fr: false, it: false, de2: false, en2: false, fr2: false, it2: false };
      this.sourceLanguage = null;
      this.targetLanguages = [];
      this.loadItem(this.selectedItem);
    },

    loadItem(val) {
      if (val && val.id) {
        this.item = {
          id: val.id,
          de: val.de?.text ?? null, en: val.en?.text ?? null,
          fr: val.fr?.text ?? null, it: val.it?.text ?? null,
          de2: val.de2?.text ?? null, en2: val.en2?.text ?? null,
          fr2: val.fr2?.text ?? null, it2: val.it2?.text ?? null,
          deId: val.de?.id ?? null, enId: val.en?.id ?? null,
          frId: val.fr?.id ?? null, itId: val.it?.id ?? null,
          de2Id: val.de2?.id ?? null, en2Id: val.en2?.id ?? null,
          fr2Id: val.fr2?.id ?? null, it2Id: val.it2?.id ?? null,
        };
      } else {
        this.item = EMPTY();
      }
    },

    async translate() {
      if (!this.validate()) return;
      this.isTranslating = true;
      const srcKey = this.sourceLanguage.kuerzel;
      const targets = this.targetLanguages.map((l) => l.deepl_kuerzel);

      try {
        // Bezeichnung (Richtung 1)
        const res1 = await AuswahlwerteApi.post("/translate", {
          multi_translation_request: {
            sourceLanguage: this.sourceLanguage.deepl_kuerzel,
            targetLanguageList: targets,
            sourceText: this.item[srcKey],
          },
          de: { id: this.item.deId, text: this.item.de, language: "1751CDBCE1E" },
          en: { id: this.item.enId, text: this.item.en, language: "180BC941F1F" },
          fr: { id: this.item.frId, text: this.item.fr, language: "186559F7DBF" },
          it: { id: this.item.itId, text: this.item.it, language: "187B79C94AC" },
        });
        if (res1.data.de) this.item.de = res1.data.de.text;
        if (res1.data.en) this.item.en = res1.data.en.text;
        if (res1.data.fr) this.item.fr = res1.data.fr.text;
        if (res1.data.it) this.item.it = res1.data.it.text;

        // Bezeichnung2 (Richtung 2)
        const res2 = await AuswahlwerteApi.post("/translate", {
          multi_translation_request: {
            sourceLanguage: this.sourceLanguage.deepl_kuerzel,
            targetLanguageList: targets,
            sourceText: this.item[srcKey + "2"],
          },
          de: { id: this.item.de2Id, text: this.item.de2, language: "1751CDBCE1E" },
          en: { id: this.item.en2Id, text: this.item.en2, language: "180BC941F1F" },
          fr: { id: this.item.fr2Id, text: this.item.fr2, language: "186559F7DBF" },
          it: { id: this.item.it2Id, text: this.item.it2, language: "187B79C94AC" },
        });
        if (res2.data.de) this.item.de2 = res2.data.de.text;
        if (res2.data.en) this.item.en2 = res2.data.en.text;
        if (res2.data.fr) this.item.fr2 = res2.data.fr.text;
        if (res2.data.it) this.item.it2 = res2.data.it.text;
      } catch (e) {
        this.$notify(apiErrorToAlert(e));
      } finally {
        this.isTranslating = false;
      }
    },

    validate() {
      const langs = ["de", "en", "fr", "it"];
      let valid = true;
      langs.forEach((lang) => {
        const has1 = !!this.item[lang];
        const has2 = !!this.item[lang + "2"];
        this.errors[lang] = has2 && !has1;
        this.errors[lang + "2"] = has1 && !has2;
        if (this.errors[lang] || this.errors[lang + "2"]) valid = false;
      });
      return valid;
    },

    buildPayload() {
      const isEdit = !!this.item.id;
      return {
        id: this.item.id,
        bezeichnung: this.item.de,
        bezeichnung2: this.item.de2,
        de:  { id: this.item.deId,  text: this.item.de,  language: "1751CDBCE1E" },
        en:  { id: this.item.enId,  text: this.item.en,  language: "180BC941F1F" },
        fr:  { id: this.item.frId,  text: this.item.fr,  language: "186559F7DBF" },
        it:  { id: this.item.itId,  text: this.item.it,  language: "187B79C94AC" },
        de2: { id: this.item.de2Id, text: this.item.de2, language: "1751CDBCE1E" },
        en2: { id: this.item.en2Id, text: this.item.en2, language: "180BC941F1F" },
        fr2: { id: this.item.fr2Id, text: this.item.fr2, language: "186559F7DBF" },
        it2: { id: this.item.it2Id, text: this.item.it2, language: "187B79C94AC" },
      };
    },

    save() {
      if (!this.validate()) return;
      const payload = this.buildPayload();
      const request = this.item.id
        ? AppApi.put("auswahlwerte/relationstyp", payload, { params: { id: this.item.id } })
        : AppApi.post("auswahlwerte/relationstyp", payload);

      request
        .then(() => {
          this.$emit("confirmed");
          this.closeModal();
          this.$notify({ type: "success", title: this.$t("global.actionsuccessful") });
        })
        .catch((e) => {
          this.$notify(apiErrorToAlert(e));
        });
    },

    closeModal() {
      this.$bvModal.hide(this.id);
    },
  },
};
</script>

<style>
.modal-backdrop {
  opacity: 0.5 !important;
}
</style>
